/**
 * Custom React Hook for fetching compliance snapshot data
 * Includes loading states and error handling
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchComplianceSnapshot } from '@/api/complianceApi';
import type { ComplianceSnapshotResponse } from '@/api/complianceApi';

interface UseComplianceResult {
  data: ComplianceSnapshotResponse['data'] | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useCompliance(): UseComplianceResult {
  const [data, setData] = useState<ComplianceSnapshotResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const snapshot = await fetchComplianceSnapshot();
      setData(snapshot);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch compliance data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
